import { AnamnesisPerClinic } from 'src/@types/Anamnesis'
import {
  ClinicDocument,
  DocumentHistoryEntry,
  DocumentStatus,
  DocumentType,
  DocumentsClinic,
} from 'src/@types/ClinicDocuments'
import { AnaMode } from 'src/hooks/useAnaNavigation'

// Copy lives in the remote translation backend; these are the keys it is looked up by. The key is derived
// from the step name so a step the backend adds needs a translation entry, not a change here.
const camelCase = (step: string) =>
  step
    .toLowerCase()
    .split('_')
    .map((word, idx) => (idx === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('')

// Three keys predate the derivation and do not spell out the whole step name. Kept so their existing copy
// keeps resolving; nothing needs to be added here for a new step.
const LEGACY_STEP_LABEL_KEY: Record<string, string> = {
  DATA_PROTECTION_AGREEMENT_ACCEPTED: 'b2c.documents.steps.dataProtectionAgreement',
  TARIFF_RESTRICTION_WAIVER: 'b2c.documents.steps.tariffWaiver',
  COST_PLAN_CREATION_FEE_AGREEMENT: 'b2c.documents.steps.costPlanFeeAgreement',
}

export const documentStepLabelKey = (step: string): string =>
  LEGACY_STEP_LABEL_KEY[step] ?? `b2c.documents.steps.${camelCase(step)}`

/**
 * What to show when the translation backend has no copy for the step yet - "CANCELLATION_FEE_CONSENT"
 * becomes "Cancellation fee consent". Untranslated, so it reads as English until the entry is added, but
 * a new step lands as something legible rather than a raw key or a blank row.
 */
export const documentStepFallbackLabel = (step: string): string => {
  const words = step.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const DOCUMENT_STATUS_LABEL_KEY: Record<DocumentStatus, string> = {
  [DocumentStatus.COMPLETE]: 'b2c.documents.statuses.ready',
  [DocumentStatus.MISSING]: 'b2c.documents.statuses.missing',
  [DocumentStatus.EXPIRED]: 'b2c.documents.statuses.outdated',
  [DocumentStatus.NOT_REQUIRED]: 'b2c.documents.statuses.notApplicable',
}

export type ClinicRollup = {
  ready: number
  total: number
  allGood: boolean
}

export const isApplicable = (doc: ClinicDocument) => doc.status !== DocumentStatus.NOT_REQUIRED

export const formatClinicAddress = (clinic: DocumentsClinic) => {
  const line = [clinic.street, clinic.houseNumber].filter(Boolean).join(' ').trim()
  const tail = [clinic.zipCode?.trim(), clinic.city?.trim()].filter(Boolean).join(' ')
  return [line, tail].filter(Boolean).join(', ')
}

// What the payload says the step is: a document is signed and viewed as a PDF, a form is filled in
// through the anamnesis flow. Neither needs the step's name.
const isSignedDocument = (doc: ClinicDocument) => doc.type === DocumentType.DOCUMENT
const isForm = (doc: ClinicDocument) => doc.type === DocumentType.FORM

// Whether the clinic actually requires a factoring agreement. Looked up
// from the sibling FACTORING_AGREEMENT entry in the same clinic's doc list;
// `NOT_REQUIRED` means the clinic doesn't need one, so the anamnesis flow
// should skip the FactoringAgreement step. If the entry is missing entirely,
// we conservatively assume it IS required (legacy behavior).
// The step is named here rather than anywhere shared: it is the anamnesis flow that cares about it.
const FACTORING_AGREEMENT_STEP = 'FACTORING_AGREEMENT'

const isFactoringRequired = (siblingDocs: ClinicDocument[]): boolean => {
  const factoring = siblingDocs.find((d) => d.step === FACTORING_AGREEMENT_STEP)
  if (!factoring) return true
  return factoring.status !== DocumentStatus.NOT_REQUIRED
}

// Which anamnesis flow a click on a form row starts: NEW when there is nothing of the clinic's own to
// prefill from, RENEW when its record has to be redone, and null when the record is current and the row
// only opens the read-only PDF. Both the href and the "offer to reuse another clinic's data" decision
// read it, so the two cannot drift apart.
export const anamnesisFlowMode = (doc: ClinicDocument): AnaMode.NEW | AnaMode.RENEW | null => {
  if (!isForm(doc)) return null
  if (!doc.entityReferenceId) return AnaMode.NEW
  if (doc.status === DocumentStatus.EXPIRED || doc.status === DocumentStatus.MISSING) return AnaMode.RENEW

  return null
}

/**
 * Whether to offer the patient the anamnesis they already filled in at another clinic as the starting
 * point. The prompt only makes sense while this clinic's form is still to be filled in or renewed - a
 * current record just opens read-only - and only when the valid record sits at a *different* clinic,
 * since the clinic's own record is what the RENEW flow prefills from anyway.
 */
export const offersAnaReuse = (
  doc: ClinicDocument,
  clinicReferenceId: string,
  mostRecentValidAna?: AnamnesisPerClinic
): boolean => {
  if (!anamnesisFlowMode(doc)) return false
  if (!mostRecentValidAna?.anamnesisReferenceId) return false

  return mostRecentValidAna.clinicReferenceId !== clinicReferenceId
}

// Per-status routing:
//   no entityReferenceId           → /anamnesis NEW (no prefill, clinic id only)
//   MISSING/EXPIRED + entityRefId  → /anamnesis RENEW prefilled from /anamnesis-histories
//   COMPLETE                       → view-only signed PDF at /documents/anamnesis-history
// We pass the entityReferenceId as `historyReferenceId` (not `referenceId`)
// because it's an anamnesis-history id, distinct from the legacy
// anamnesisReferenceId the OLD endpoint expects. The Anamnesis screen reads
// that query param and routes the prefill fetch through the new endpoint.
// URLs are constructed in the post-middleware format to avoid relying on
// middleware.ts firing on client-side router.push.
const buildAnamnesisHref = (
  doc: ClinicDocument,
  clinicReferenceId: string,
  siblingDocs: ClinicDocument[]
): string => {
  const factoringRequiredSuffix = isFactoringRequired(siblingDocs) ? '' : '&factoringRequired=false'

  switch (anamnesisFlowMode(doc)) {
    case AnaMode.NEW:
      return `/anamnesis/referenceId/${AnaMode.NEW}?referenceId=${clinicReferenceId}${factoringRequiredSuffix}`
    case AnaMode.RENEW:
      return `/anamnesis/referenceId/${AnaMode.RENEW}?historyReferenceId=${doc.entityReferenceId}${factoringRequiredSuffix}`
    default:
      // Current (valid) anamnesis: the detail page may offer "Edit" — flag it so historic
      // versions (reached via buildHistoryVersionHref, without the flag) stay read-only.
      return `/documents/anamnesis-history/${doc.entityReferenceId}?editable=true`
  }
}

// Edit the current anamnesis: start the form in EDIT mode, prefilled from the
// anamnesis-history record. On submit a new version is created and the old one
// is invalidated (the prior "Edit" behavior). Post-middleware URL format.
export const buildAnamnesisEditHref = (anamnesisHistoryReferenceId: string): string =>
  `/anamnesis/referenceId/${AnaMode.EDIT}?historyReferenceId=${anamnesisHistoryReferenceId}`

export const buildDocumentHref = (
  doc: ClinicDocument,
  clinicReferenceId: string,
  siblingDocs: ClinicDocument[]
): string | null => {
  if (isForm(doc)) return buildAnamnesisHref(doc, clinicReferenceId, siblingDocs)
  if (isSignedDocument(doc)) return `/documents/${clinicReferenceId}/${doc.step}`
  return null
}

// Historic versions exist for whatever gets signed - the anamnesis and every document. The remaining
// form steps are flags rather than records, so there is nothing to list and we do not query for them.
export const stepSupportsHistory = (doc: ClinicDocument): boolean => doc.requiresSignature

// Read-only PDF view for one historic version:
//   form (the anamnesis) → existing /documents/anamnesis-history/{referenceId}
//   signed document      → /documents/{clinicId}/{step}/history/{referenceId}
export const buildHistoryVersionHref = (
  doc: ClinicDocument,
  entry: DocumentHistoryEntry,
  clinicReferenceId: string
): string => {
  if (isForm(doc)) {
    return `/documents/anamnesis-history/${entry.referenceId}`
  }
  return `/documents/${clinicReferenceId}/${entry.step}/history/${entry.referenceId}`
}

// Past versions are only ever Expired or Invalidated.
export const historyVersionLabelKey = (entry: DocumentHistoryEntry): string =>
  entry.expired ? 'b2c.documents.versionStatus.expired' : 'b2c.documents.versionStatus.invalidated'

export const historyVersionSubtitleKey = (doc: ClinicDocument): string =>
  isForm(doc) ? 'b2c.documents.versionSubmitted' : 'b2c.documents.versionSigned'

export const buildRollup = (documents: ClinicDocument[]): ClinicRollup => {
  const applicable = documents.filter(isApplicable)
  const total = applicable.length
  const ready = applicable.filter((d) => d.status === DocumentStatus.COMPLETE).length
  const allGood = total > 0 && ready === total

  return { ready, total, allGood }
}
